import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import {
  AcceptedFileTypes,
  SelectedDocument,
} from "@/components/common/inputs/FileUpload";
import { MenuItem, Select, TextField } from "@mui/material";
import S3FileUpload from "@/components/common/inputs/S3FileUpload";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import DynamicAutocomplete from "@/components/common/inputs/select/DynamicAutocomplete";

interface AddIMDocumentProps {
  onClose: () => void;
  onSubmit: () => void;
  isModalOpen: boolean;
  folderId: number;
  initialData?: any;
  mode?: "create" | "edit" | "upload";
  selectedDocument?: SelectedDocument | null;
}

interface UserListItem {
  id: string;
  name: string;
  // add other fields as needed
}

interface FileUploadPayload {
  id?: number;
  folder_id?: number;
  file_name?: string;
  s3_key?: string;
  version_id?: string;
  size?: number;
}

interface FormValues {
  superTrader: string;
  documentTitle: string;
  recipient: UserTierType.Tier1 | UserTierType.Tier2;
  selectedProvider: string;
  file_name: string;
  s3_key: string;
  version_id: string;
  size: number;
}

const AddIMDocument: React.FC<AddIMDocumentProps> = ({
  onClose,
  onSubmit,
  isModalOpen,
  folderId,
  initialData,
  mode = "create",
  selectedDocument,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const [superTraders, setSuperTraders] = useState<UserListItem[]>([]);
  const [providers, setProviders] = useState<UserListItem[]>([]);
  const [uploadedFileDetails, setUploadedFileDetails] =
    useState<SelectedDocument | null>(selectedDocument || null);
  const [isPageLoading, setIsPageLoading] = useState(true);

  const validationSchema = Yup.object({
    superTrader: Yup.string().required("Super trader is missing"),
    documentTitle: Yup.string()
      .required("Document title is missing")
      .matches(/^[a-zA-Z0-9\s]*$/, "Only alphanumeric characters are allowed")
      .max(100, "Document title maximum limit of 100 characters"),
    recipient: Yup.string().required("Recipient type is missing"),
    selectedProvider: Yup.string().required("Recipient User is missing"),
    file_name: Yup.string().required("Document is missing"),
  });

  const handleFileUploadSuccess = (payload: SelectedDocument) => {
    setUploadedFileDetails(payload);

    // Update formik values with file details
    formik.setFieldValue("file_name", payload.file_name);
    formik.setFieldValue("s3_key", payload.s3_key);
    formik.setFieldValue("version_id", payload.version_id);
    formik.setFieldValue("size", payload.size);
  };

  const handleSubmitAction = async (isSend: boolean) => {
    try {
      // Touch all fields to trigger validation
      Object.keys(formik.values).forEach((key) => {
        formik.setFieldTouched(key, true);
      });

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        return;
      }

      setIsLoading(true);

      if (!uploadedFileDetails) {
        throw new Error("Please upload a file first");
      }

      // Determine API endpoint based on mode
      const { authenticatedCall } = actionsHandler(
        mode === "edit" ? "updateIMDocument" : "addIMDocument",
      );

      const payload = {
        user_id: formik.values.superTrader,
        folder_id: folderId,
        document_title: formik.values.documentTitle,
        file_name: uploadedFileDetails.file_name,
        s3_key: uploadedFileDetails.s3_key,
        size: uploadedFileDetails.size,
        funder_id: formik.values.selectedProvider,
        version_id: uploadedFileDetails.version_id,
        is_send: isSend,
      };

      const documentResponse = await dispatch(
        authenticatedCall({
          method: mode === "edit" ? "put" : "post",
          payload,
          ...(mode === "edit" && { customParams: [initialData.id + "/"] }),
        }),
      );

      if (documentResponse.ok) {
        toast.success(
          documentResponse.data.message ||
            `Document ${isSend ? "sent" : mode === "edit" ? "updated" : "added"} successfully`,
        );
        formik.resetForm();
        setUploadedFileDetails(null);
        onSubmit();
        onClose();
      } else {
        throw new Error(
          `Failed to ${isSend ? "send" : mode === "edit" ? "update" : "add"} document`,
        );
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      superTrader: initialData?.super_trader.id || "",
      documentTitle: initialData?.selected_document.document_title || "",
      recipient: initialData?.funder.user_type || UserTierType.Tier1,
      selectedProvider: initialData?.funder.id || "",
      file_name: initialData?.selected_document.file_name || "",
      s3_key: initialData?.selected_document.s3_key || "",
      version_id: initialData?.selected_document.version_id || "",
      size: initialData?.selected_document.size || 0,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      // ... existing submit logic ...
    },
  });

  // Set uploaded file details if initialData exists
  useEffect(() => {
    if (initialData && mode === "edit") {
      let selectedDocument = {
        file_name: initialData.selected_document.file_name,
        s3_key: initialData.selected_document.s3_key,
        version_id: initialData.selected_document.version_id,
        size: initialData.selected_document.size,
        folder_id: folderId,
        id: initialData.id,
        status: initialData.selected_document.status,
      };
      setUploadedFileDetails(selectedDocument);
      console.log("initialData: ", initialData);
      formik.setValues({
        ...formik.values,
        superTrader: initialData?.super_trader.id || "",

        documentTitle: initialData?.selected_document.document_title || "",
        recipient: initialData?.funder.user_type || UserTierType.Tier1,
        selectedProvider: initialData?.funder.id || "",
        file_name: initialData?.selected_document.file_name || "",
        s3_key: initialData?.selected_document.s3_key || "",
        version_id: initialData?.selected_document.version_id || "",
        size: initialData?.selected_document.size || 0,
      });
    }
  }, [initialData, mode, folderId]);

  // Fetch super traders (T4)
  const fetchSuperTraders = async () => {
    try {
      const { authenticatedCall } = actionsHandler("userList");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: ["4"], // T4 users
        }),
      );
      if (response?.ok) {
        setSuperTraders(response.data);
      }
    } catch (error) {
      console.error("Error fetching super traders:", error);
    }
  };

  // Fetch providers based on radio selection
  const fetchProviders = async (recipientType: UserTierType) => {
    try {
      const { authenticatedCall } = actionsHandler("userList");
      const tierId = recipientType === UserTierType.Tier1 ? "1" : "2";
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [tierId],
        }),
      );
      if (response?.ok) {
        // Keep existing providers and update only for the current recipient type
        setProviders(response.data);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  // Update useEffect for recipient change
  useEffect(() => {
    if (formik.values.recipient) {
      fetchProviders(formik.values.recipient);
    }
  }, [formik.values.recipient]);

  // Separate useEffect for data fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchSuperTraders(),
          fetchProviders(formik.values.recipient),
        ]);
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setIsPageLoading(false);
      }
    };

    // Only fetch data if modal is open
    if (isModalOpen) {
      fetchData();
    }
  }, [isModalOpen]);

  // Update header text based on mode
  const headerText = mode === "edit" ? "Edit IM Document" : "Add IM Document";

  // Update button text based on mode
  const submitButtonText = mode === "edit" ? "Update" : "Add";

  const handleRecipientChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRecipientType = Number(event.target.value) as UserTierType;
    formik.setFieldValue("recipient", newRecipientType);
    formik.setFieldValue("selectedProvider", "");
  };

  return (
    <div
      className={`tw-w-[350px] tw-fixed tw-bg-black/25 tw-top-0 tw-min-h-screen tw-flex tw-items-center tw-justify-end tw-z-50 tw-transition-all tw-ease-in-out tw-duration-300 ${
        isModalOpen ? "tw-right-0" : "tw-right-[-350px]"
      }`}
    >
      <div className="tw-bg-white tw-shadow-lg tw-h-screen tw-w-[350px] tw-flex tw-flex-col">
        {/* Fixed Header - Always visible */}
        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b tw-shrink-0">
          <h2 className="tw-text-base tw-font-semibold">{headerText}</h2>
          <button onClick={onClose}>
            <img src={CloseIcon} alt="Close" className="tw-w-auto tw-h-auto" />
          </button>
        </div>

        {/* Content area with loader or form */}
        <div className="tw-flex-1 tw-overflow-y-auto">
          {isPageLoading ? (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-full">
              <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-[#146C94]" />
            </div>
          ) : (
            <form
              onSubmit={formik.handleSubmit}
              className="tw-flex tw-flex-col tw-h-full"
            >
              <div className="tw-flex-1 tw-overflow-y-auto">
                <div className="tw-px-4 tw-py-4">
                  <div className="tw-space-y-6">
                    {/* Super Trader Select */}
                    <div className="form-group">
                      <DynamicAutocomplete
                        name="superTrader"
                        label="Super Trader (T4)"
                        options={superTraders}
                        selectedValue={Number(formik.values.superTrader)}
                        labelKey="name"
                        valueKey="id"
                        onChange={(value: any) => {
                          formik.setFieldValue("superTrader", value);
                        }}
                        touched={formik.touched.superTrader}
                        error={formik.errors.superTrader}
                      />
                    </div>

                    {/* Document Title */}
                    <div className="form-group">
                      <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                        Document Title
                      </label>
                      <TextField
                        fullWidth
                        name="documentTitle"
                        value={formik.values.documentTitle}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.documentTitle &&
                          Boolean(formik.errors.documentTitle)
                        }
                        placeholder="Enter document title"
                        className="tw-h-11"
                      />
                      {formik.touched.documentTitle &&
                        formik.errors.documentTitle && (
                          <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                            {String(formik.errors.documentTitle)}
                          </div>
                        )}
                    </div>

                    {/* File Upload */}
                    <div className="form-group">
                      <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                        Upload IM Document
                      </label>
                      <S3FileUpload
                        label="Upload IM Document"
                        folderId={folderId}
                        accept={[AcceptedFileTypes.PDF]}
                        disableIsNA={true}
                        maxFileSize={250 * 1000 * 1000} // 250MB in bytes
                        id="fileInput-1"
                        onSuccessUploadCallback={handleFileUploadSuccess}
                        selectedDocument={uploadedFileDetails || undefined}
                      />
                      {!uploadedFileDetails && formik.touched.file_name && (
                        <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                          Please upload a document
                        </div>
                      )}
                    </div>

                    {/* Recipient Selection */}
                    <div className="form-group">
                      <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                        Who do you want to send?
                      </label>
                      <div className="tw-space-y-2">
                        <label className="tw-flex tw-items-center tw-space-x-2">
                          <input
                            type="radio"
                            name="recipient"
                            value={UserTierType.Tier1}
                            checked={
                              formik.values.recipient === UserTierType.Tier1
                            }
                            onChange={handleRecipientChange}
                            className="tw-form-radio"
                          />
                          <span>Funders/Credit Provider (T1)</span>
                        </label>
                        <label className="tw-flex tw-items-center tw-space-x-2">
                          <input
                            type="radio"
                            name="recipient"
                            value={UserTierType.Tier2}
                            checked={
                              formik.values.recipient === UserTierType.Tier2
                            }
                            onChange={handleRecipientChange}
                            className="tw-form-radio"
                          />
                          <span>Insurance/Guarantor (T2)</span>
                        </label>
                      </div>

                      {formik.values.recipient && (
                        <div className="tw-mt-4 form-group">
                          <DynamicAutocomplete
                            name="selectedProvider"
                            label={`Select ${formik.values.recipient === UserTierType.Tier1 ? "funders/credit providers" : "insurance/guarantors"}`}
                            options={providers}
                            selectedValue={Number(
                              formik.values.selectedProvider,
                            )}
                            labelKey="name"
                            valueKey="id"
                            onChange={(value: any) => {
                              formik.setFieldValue(
                                "selectedProvider",
                                String(value),
                              );
                            }}
                            touched={formik.touched.selectedProvider}
                            error={
                              Boolean(formik.touched.selectedProvider)
                                ? formik.errors.selectedProvider
                                : undefined
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Fixed Footer/Buttons */}
              <div className="tw-px-4 tw-py-4 tw-border-t tw-mt-auto tw-shrink-0">
                <div className="tw-space-x-4 tw-flex">
                  <button
                    type="button"
                    onClick={() => handleSubmitAction(false)}
                    disabled={isPageLoading}
                    className="tw-flex-1 tw-h-11 tw-bg-[#146C94] tw-text-white tw-px-4 tw-rounded-md disabled:tw-opacity-50"
                  >
                    {submitButtonText}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSubmitAction(true)}
                    disabled={isPageLoading}
                    className="tw-flex-1 tw-h-11 tw-bg-[#146C94] tw-text-white tw-px-4 tw-rounded-md disabled:tw-opacity-50"
                  >
                    Send
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddIMDocument;
